import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Eye } from 'lucide-react';
import { ARTWORKS } from '../data/artworks';
import '../styles/inactivity-popup.css';

const INACTIVITY_MS = 60_000;
// Inclut 'joystick-activity' : les touch events du joystick utilisent stopPropagation
// et ne remontent pas au window — on dispatche manuellement depuis controls.jsx
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'joystick-activity'];

export default function InactivityPopup({ onViewArtwork, language }) {
  const isFr = language !== 'en' && language !== 'he'; // French by default
  const isHe = language === 'he';
  const [artwork, setArtwork] = useState(null);
  const isShowingRef = useRef(false);
  const timerRef = useRef(null);

  const startTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!isShowingRef.current) {
        const random = ARTWORKS[Math.floor(Math.random() * ARTWORKS.length)];
        isShowingRef.current = true;
        setArtwork(random);
      }
    }, INACTIVITY_MS);
  }, []);

  useEffect(() => {
    const onActivity = () => {
      if (!isShowingRef.current) startTimer();
    };
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    // Délai initial de 12s : laisse le temps à l'animation de porte + chargement initial
    // avant de commencer à surveiller l'inactivité
    const initDelay = setTimeout(() => startTimer(), 12_000);

  window.__testShowInactivityPopup = () => {
      const random = ARTWORKS[Math.floor(Math.random() * ARTWORKS.length)];
      isShowingRef.current = true;
      setArtwork(random);
    };

    return () => {
      clearTimeout(timerRef.current);
      clearTimeout(initDelay);
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      delete window.__testShowInactivityPopup;
    };
  }, [startTimer]);

  const handleClose = useCallback(() => {
    isShowingRef.current = false;
    setArtwork(null);
    startTimer();
  }, [startTimer]);

  const handleView = useCallback(() => {
    if (artwork) {
      isShowingRef.current = false;
      onViewArtwork(artwork);
      setArtwork(null);
    }
  }, [artwork, onViewArtwork]);

  if (!artwork) return null;

  return (
    <div className="iap-overlay" data-testid="inactivity-popup">
      <div className="iap-backdrop" onClick={handleClose} />
      <div className="iap-card" data-testid="inactivity-popup-card">
        <button
          className="iap-close"
          onClick={handleClose}
          data-testid="inactivity-popup-close"
          aria-label="Fermer"
        >
          <X size={13} />
        </button>

        <p className="iap-eyebrow">
          {isHe ? 'שמת לב...' : isFr ? 'Avez-vous remarqué…' : 'Did you notice…'}
        </p>

        <div className="iap-artwork-preview">
          <img
            src={artwork.imageUrl}
            alt={artwork.title}
            className="iap-artwork-img"
          />
          <div className="iap-artwork-glow" />
        </div>

        <h3 className="iap-artwork-title">{artwork.title}</h3>
        <p className="iap-artwork-subtitle">{artwork.subtitle}</p>

        <button
          className="iap-view-btn"
          onClick={handleView}
          data-testid="inactivity-popup-view-btn"
        >
          <Eye size={13} />
          <span>{isHe ? 'צפה ביצירה' : isFr ? 'Voir l\'œuvre' : 'View artwork'}</span>
        </button>

        <p className="iap-dismiss" onClick={handleClose}>
          {isHe ? 'המשך לחקור' : isFr ? 'Continuer à explorer' : 'Continue exploring'}
        </p>
      </div>
    </div>
  );
}
